import { useForm } from 'react-hook-form'
import { Button, Checkbox, ModalPresetConfirm, useModal } from '@dashboard/ui'
import { useGameSettingsFeaturesUpdateMutate } from '@/api/useGameSettingsFeaturesUpdateMutate'
import { dashboardClient, getErrorText } from '@/api'
import { FeatureFlags } from '@/api/dashboard'
import { FieldGroup, Page, PageHeader, ToastSeverity, useToast } from '@/ui'
import DescBlock from '@/layouts/game-settings/components/DescBlock'
import Block from '@/layouts/game-settings/components/Block'
import { KeyValue } from '@/types'
import { DangerousButton } from '@/components'
import { CopyGameModal } from '@/layouts/game-settings/widgets/CopyGameModal'
import { useTranslation } from 'react-i18next'
import { HOME_PATH, SUPER_ADMIN_PATH_RECEIPT_TEST, useGenerateCompanyPath } from '@/libs'
import { setCompanyGameId, setLastGameId } from '@/Settings'
import { useCurrentGame } from '@/api/useCompaniesQuery'
import { useGameSettingsQuery } from '@/api/useGameSettingsQuery'
import { useGameSettingsCreateMutate } from '@/api/useGameSettingsCreateMutate'
import { StoreSettingsForm } from '@/layouts/admin/widgets/StoreSettingsForm'
import { useNavigate } from 'react-router-dom'
import { TransferGameModal } from '@/layouts/game-settings/widgets/TransferGameModal'

export const AdminPage = () => {
  const { companyId, gameId, generatePath } = useGenerateCompanyPath()
  const { company } = useCurrentGame()
  const { t } = useTranslation()
  const showToast = useToast()
  const navigate = useNavigate()
  const { data: settings } = useGameSettingsQuery(companyId, gameId)

  const { mutateAsync: createMutateAsync } = useGameSettingsCreateMutate(companyId, gameId)
  const { mutateAsync: updateMutateAsync } = useGameSettingsFeaturesUpdateMutate(companyId, gameId)

  const {
    register,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<FeatureFlags>({
    values: {
      ...settings?.feature_flags,
    },
  })

  const onSaveClick = async (data: FeatureFlags) => {
    let id = settings?.id

    if (!id) {
      await createMutateAsync({
        data: {},
      })
      let { data: createdSettings } = await dashboardClient.v1.getGameSettings(companyId, gameId)
      id = createdSettings.id
    }

    for (let key in data) {
      let map = data as KeyValue
      if (map[key] === false) {
        map[key] = null
      }
    }

    await updateMutateAsync({
      settingsId: id,
      data: {
        feature_flags: data,
      },
    })

    showToast({ message: t('saved'), severity: ToastSeverity.success })
  }

  const onDiscardClick = () => {
    reset()
  }

  const onConfirmArchive = async () => {
    await dashboardClient.v1.deleteGame(companyId, gameId)

    const otherGame = company?.games?.find(g => g.id != gameId)
    if (otherGame) {
      window.location.href = generatePath(HOME_PATH, { companyId: companyId, gameId: otherGame.id })
    } else {
      setLastGameId(null)
      setCompanyGameId(null)
      window.location.href = '/'
    }
  }

  const openConfirmArchive = useModal(props => (
    <ModalPresetConfirm
      {...props}
      title={{
        children: t('game-settings.archive-game.title'),
        subtitle: t('game-settings.archive.confirm'),
      }}
      cancel={{
        label: t('confirm.no'),
      }}
      submit={{
        color: 'danger',
        callback: () => {
          props.onClose()
          onConfirmArchive()
        },
        label: t('game-settings.archive'),
      }}
    />
  ))

  const openCopyGameModal = useModal(props => <CopyGameModal {...props} />)
  const openTransferGameModal = useModal(props => <TransferGameModal {...props} />)

  return (
    <Page>
      <PageHeader variant="bordered" sticky={true}>
        Admin
      </PageHeader>

      <div className="mb-6 mt-2 flex flex-col gap-8">
        <DescBlock title="Features flags" description="Set Features flags for current game">
          <Block className="flex flex-col gap-3">
            {Object.keys(settings?.feature_flags || {}).map(key => (
              <Checkbox {...register(key as keyof FeatureFlags)} key={key}>
                {key}
              </Checkbox>
            ))}
            <div className="flex justify-end gap-2">
              {isDirty && (
                <Button variant="outline" color="secondary" size="sm" onClick={onDiscardClick}>
                  {t('Discard')}
                </Button>
              )}
              <Button size="sm" disabled={!isDirty} onClick={handleSubmit(onSaveClick)}>
                {t('Save2')}
              </Button>
            </div>
          </Block>
        </DescBlock>

        <DescBlock title={t('game-settings.game-operations')}>
          <div className="flex flex-col gap-3">
            <Block className="flex flex-col gap-4 p-6">
              <div className="flex justify-between">
                <FieldGroup label={t('game-settings.copy-game.title')}>
                  <div className="text-text-tertiary">{t('game-settings.copy-game.subtitle')}</div>
                </FieldGroup>
                <div className="flex min-w-[100px] items-center justify-end">
                  <Button variant="outline" color="primary" onClick={openCopyGameModal}>
                    {t('game-settings.copy-game')}
                  </Button>
                </div>
              </div>

              <div className="flex justify-between">
                <FieldGroup label={t('game-settings.transfer-game.title')}>
                  <div className="text-text-tertiary">{t('game-settings.transfer-game.subtitle')}</div>
                </FieldGroup>
                <div className="flex min-w-[100px] items-center justify-end">
                  <Button variant="outline" color="danger" onClick={openTransferGameModal}>
                    {t('game-settings.transfer-game')}
                  </Button>
                </div>
              </div>
            </Block>

            <DangerousButton
              onClick={() => openConfirmArchive()}
              btnText={t('game-settings.archive-game')}
              title={t('game-settings.archive-game.title')}
              subTitle={t('game-settings.archive-game.subtitle')}
              color={'danger'}
            />
          </div>
        </DescBlock>

        <StoreSettingsForm />

        <DescBlock title={'Receipt email'}>
          <div className="pt-4">
            <Button onClick={() => navigate(generatePath(SUPER_ADMIN_PATH_RECEIPT_TEST))} color={'primary'}>
              Open receipt email test
            </Button>
          </div>
        </DescBlock>

        <DescBlock title={'Dashboard analytics cache'}>
          <div className="flex flex-col gap-3">
            <DangerousButton
              onClick={async () => {
                await dashboardClient.v1.clearAnalyticsCacheByGame(companyId, gameId)
                showToast({ message: 'Done', severity: ToastSeverity.success })
              }}
              btnText={'Clear cache for CURRENT GAME'}
              title={'Dashboard analytics cache'}
              subTitle={'Dashboard analytics cache for current games will be cleared.'}
              color={'danger'}
            />

            <DangerousButton
              onClick={async () => {
                await dashboardClient.v1.clearAnalyticsCache()
                showToast({ message: 'Done', severity: ToastSeverity.success })
              }}
              btnText={'Clear cache for ALL GAMES'}
              title={'Dashboard analytics cache'}
              subTitle={'Dashboard analytics cache for all games will be cleared.'}
              color={'danger'}
            />
          </div>
        </DescBlock>

        <DescBlock title={'Leaderboards'}>
          <div className="flex flex-col gap-3">
            <DangerousButton
              onClick={async () => {
                try {
                  await dashboardClient.v1.resetLeaderboardAvatars(companyId, gameId)
                  showToast({ message: 'Done', severity: ToastSeverity.success })
                } catch (e) {
                  showToast({ message: getErrorText(e) || 'Something went wrong', severity: ToastSeverity.error })
                }
              }}
              btnText={'Reset avatars'}
              title={'Reset leaderboard avatars'}
              subTitle={'Replace avatars in leaderboards with avatars randomly selected from the website.'}
              color={'danger'}
            />
          </div>
        </DescBlock>
      </div>
    </Page>
  )
}
